/**
 * Code Injection modal — head/body code textareas for analytics, chat widgets, etc.
 * Raw injection into exported HTML. NOT shown in iframe preview.
 */

import { store } from '../core/state.js';
// The kit, not shell.js. This module only ever wanted two modal primitives, and
// reaching them through the app shell dragged the whole shell into its import
// graph — the primitives live in components.js and always did.
import { createModal } from './components.js';
import { escapeHTML } from '../shared/html-escape.js';

export function showCodeInjectionModal() {
  if (document.querySelector('.pb-code-injection-overlay')) return;

  const state = store.getState();

  const { body } = createModal({
    title: 'Code Injection',
    maxWidth: 600,
    overlayClassName: 'pb-code-injection-overlay',
    withFooter: false,
    initialFocus: '#pb-head-code',
    // Committing the open transaction is the whole reason this dialog has a
    // close path of its own: leaving it open would let the NEXT unrelated edit
    // land in the same undo entry as the code the user just typed.
    onClose: () => store.commitTransaction()
  });

  body.innerHTML = `
    <div class="pb-code-tabs">
      <button class="pb-code-tab pb-active" data-tab="head">Head</button>
      <button class="pb-code-tab" data-tab="body">Body</button>
    </div>
    <div class="pb-code-tab-content pb-code-tab-content-active" data-content="head">
      <p class="pb-props-hint">Injected before <code>&lt;/head&gt;</code> in exported HTML. Not shown in preview.</p>
      <textarea class="pb-code-textarea" id="pb-head-code" rows="12" spellcheck="false" aria-label="Head code injected before closing head tag" placeholder="<!-- Analytics, meta tags, etc. -->">${escapeHTML(state.headCode || '')}</textarea>
    </div>
    <div class="pb-code-tab-content" data-content="body">
      <p class="pb-props-hint">Injected before <code>&lt;/body&gt;</code> in exported HTML. Not shown in preview.</p>
      <textarea class="pb-code-textarea" id="pb-body-code" rows="12" spellcheck="false" aria-label="Body code injected before closing body tag" placeholder="<!-- Chat widgets, tracking pixels, etc. -->">${escapeHTML(state.bodyCode || '')}</textarea>
    </div>
  `;

  // Tab switching
  const tabs = body.querySelectorAll('.pb-code-tab');
  const contents = body.querySelectorAll('.pb-code-tab-content');
  for (const tab of tabs) {
    tab.addEventListener('click', () => {
      for (const t of tabs) t.classList.remove('pb-active');
      tab.classList.add('pb-active');
      const nextContent = body.querySelector(
        `.pb-code-tab-content[data-content="${tab.dataset.tab}"]`
      );

      for (const c of contents) {
        c.classList.toggle('pb-code-tab-content-active', c === nextContent);
      }
    });
  }

  // Textareas with transaction-based undo batching
  const headTA = body.querySelector('#pb-head-code');
  const bodyTA = body.querySelector('#pb-body-code');

  bindTextarea(headTA, 'headCode', 'Edit head code');
  bindTextarea(bodyTA, 'bodyCode', 'Edit body code');
}

function bindTextarea(el, stateKey, label) {
  if (!el) return;
  el.addEventListener('focus', () => store.beginTransaction(label));
  el.addEventListener('blur', () => store.commitTransaction());
  el.addEventListener('input', () => {
    store.update(stateKey, el.value, label);
  });
}
